import { medianOvr } from '../matchmaking/teams';
import type { Pair, RatedPlayer } from '../matchmaking/types';
import { ISO_GAMES_EACH } from './catalog';

/**
 * Iso Gauntlet pairings. Every player gets up to `gamesEach` different opponents (three by
 * default, fewer in a small crew), matched by OVR so the games are close. An unrated player counts
 * as the median of the rated ones (same rule as the team balancer). Odd crews rest one player per
 * round, the one who has played the most. Pure and deterministic.
 *
 * Up to 12 players the best matching of each round is found exactly; above that it falls back to
 * pairing neighbours by OVR.
 */

export interface IsoMatch {
  a: string;
  b: string;
  round: number;
}

export interface IsoSchedule {
  matches: IsoMatch[];
  /** Games each player is meant to play (three by default). */
  gamesEach: number;
}

export interface IsoOptions {
  gamesEach?: number;
  /** Pairs that met lately. They cost REPEAT_COST extra, so fresh matchups come first. */
  recentPairs?: readonly Pair[];
}

const REPEAT_COST = 6;
/** A pair that already played in this schedule. Large, so a rematch only happens when nobody fresh is left. */
const REMATCH_COST = 1000;
const EXACT_LIMIT = 12;

const key = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);

export function isoSchedule(players: readonly RatedPlayer[], opts: IsoOptions = {}): IsoSchedule {
  const n = players.length;
  const gamesEach = Math.max(1, opts.gamesEach ?? ISO_GAMES_EACH);
  if (n < 2) return { matches: [], gamesEach };
  const fallback = medianOvr(players);
  const ovr = new Map(players.map((p) => [p.id, p.ovr ?? fallback]));
  const recent = new Set((opts.recentPairs ?? []).map(([a, b]) => key(a, b)));
  const order = [...players].sort((a, b) => (ovr.get(b.id) as number) - (ovr.get(a.id) as number)).map((p) => p.id);

  const played = new Set<string>();
  const count = new Map(order.map((id) => [id, 0]));
  const matches: IsoMatch[] = [];
  let round = 0;

  const cost = (a: string, b: string) =>
    Math.abs((ovr.get(a) as number) - (ovr.get(b) as number)) +
    (recent.has(key(a, b)) ? REPEAT_COST : 0) +
    (played.has(key(a, b)) ? REMATCH_COST : 0);

  const add = (a: string, b: string) => {
    matches.push({ a, b, round });
    played.add(key(a, b));
    count.set(a, (count.get(a) as number) + 1);
    count.set(b, (count.get(b) as number) + 1);
  };

  for (round = 1; round <= gamesEach * 3; round++) {
    let pool = order.filter((id) => (count.get(id) as number) < gamesEach);
    if (pool.length < 2) break;
    if (pool.length % 2 === 1) {
      // Rest the player who has played the most; the rest is matched exactly.
      const most = Math.max(...pool.map((id) => count.get(id) as number));
      let sitter: string | null = null;
      let best = Number.POSITIVE_INFINITY;
      for (const s of pool.filter((id) => (count.get(id) as number) === most)) {
        const c = bestMatching(
          pool.filter((id) => id !== s),
          cost,
        ).cost;
        if (c < best) {
          best = c;
          sitter = s;
        }
      }
      pool = pool.filter((id) => id !== sitter);
    }
    for (const [a, b] of bestMatching(pool, cost).pairs) add(a, b);
  }

  // One player left short (an odd crew): a game against whoever has played least, fresh face first.
  const short = order.find((id) => (count.get(id) as number) < gamesEach);
  if (short) {
    const rest = order
      .filter((id) => id !== short)
      .sort(
        (x, y) =>
          Number(played.has(key(short, x))) - Number(played.has(key(short, y))) ||
          (count.get(x) as number) - (count.get(y) as number),
      );
    for (const opp of rest) {
      if ((count.get(short) as number) >= gamesEach) break;
      add(short, opp);
    }
  }
  return { matches, gamesEach };
}

function bestMatching(
  ids: readonly string[],
  cost: (a: string, b: string) => number,
): { pairs: [string, string][]; cost: number } {
  if (ids.length > EXACT_LIMIT) {
    // Too many for the exact search: each player in OVR order takes the cheapest partner left.
    const pairs: [string, string][] = [];
    const used = new Set<string>();
    let total = 0;
    for (const x of ids) {
      if (used.has(x)) continue;
      let pick: string | null = null;
      let pickCost = Number.POSITIVE_INFINITY;
      for (const y of ids) {
        if (y === x || used.has(y)) continue;
        const c = cost(x, y);
        if (c < pickCost) {
          pick = y;
          pickCost = c;
        }
      }
      if (pick === null) continue;
      used.add(x);
      used.add(pick);
      pairs.push([x, pick]);
      total += pickCost;
    }
    return { pairs, cost: total };
  }
  let best: { pairs: [string, string][]; cost: number } = { pairs: [], cost: Number.POSITIVE_INFINITY };
  const taken = new Array<boolean>(ids.length).fill(false);
  const current: [string, string][] = [];
  const go = (acc: number) => {
    if (acc >= best.cost) return;
    const i = taken.indexOf(false);
    if (i < 0) {
      best = { pairs: [...current], cost: acc };
      return;
    }
    taken[i] = true;
    for (let j = i + 1; j < ids.length; j++) {
      if (taken[j]) continue;
      taken[j] = true;
      current.push([ids[i] as string, ids[j] as string]);
      go(acc + cost(ids[i] as string, ids[j] as string));
      current.pop();
      taken[j] = false;
    }
    taken[i] = false;
  };
  go(0);
  return best;
}

/** Games a player is in, in the schedule. */
export function gamesOf(schedule: IsoSchedule, playerId: string): IsoMatch[] {
  return schedule.matches.filter((m) => m.a === playerId || m.b === playerId);
}
